// /admin/v2/devices — the laptops/browsers signed in to the student app (BRIEFS C3.4,
// SPEC §5/§7 "Devices (sign out)"). Students never sign out themselves; only here.
// Signing out stops the device at its next renewal (within 60 minutes, decision D-34).
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { errText, fmtDateTime, v2DeviceRevoke, v2Devices } from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';

export default function AdminV2Devices() {
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');
  const q = useQuery({ queryKey: ['v2a', 'devices'], queryFn: v2Devices });
  const rows = Array.isArray(q.data) ? q.data : [];
  const active = rows.filter((r) => !r.revoked).length;

  const revoke = async (r) => {
    if (!window.confirm(`Sign out "${r.device_name || 'this device'}" for ${r.full_name || r.username}? They will need their password again on it.`)) return;
    setBusy(r.id); setMsg(null);
    try {
      await v2DeviceRevoke(r.id);
      setMsg({ kind: 'ok', text: `Signed out. The device stops working within 60 minutes and then shows the sign-in page.` });
      q.refetch();
    } catch (x) { setMsg({ kind: 'err', text: errText(x, 'Could not sign the device out.') }); }
    finally { setBusy(''); }
  };

  return (
    <div>
      <V2Tabs group="student" title="Devices" intro="Every laptop or browser where a student signed in to the app. A device stays signed in for ever until you sign it out here." />
      <Msg msg={msg} />
      <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 8 }} data-testid="v2dev-count">
        {active} signed in · {rows.length - active} signed out
      </div>
      {q.isLoading ? <div>Loading…</div> : q.isError ? (
        <div className="card" style={{ padding: 18, color: 'var(--danger)' }}>{errText(q.error, 'Could not load the devices.')}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }} data-testid="v2dev-list">
            <thead><tr><th>Student</th><th>Device</th><th>Signed in on</th><th>Last used</th><th>Status</th><th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ opacity: r.revoked ? 0.6 : 1 }}>
                  <td>{r.full_name || r.username} <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>({r.username})</span></td>
                  <td style={{ fontSize: 13 }}>{r.device_name || '—'}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDateTime(r.last_seen_at)}</td>
                  <td>
                    {r.revoked
                      ? <span className="badge badge-yellow">Signed out {fmtDateTime(r.revoked_at)}</span>
                      : <span className="badge badge-green">Signed in</span>}
                  </td>
                  <td>
                    {!r.revoked && (
                      <button className="btn btn-danger btn-sm" disabled={busy === r.id} onClick={() => revoke(r)} style={{ whiteSpace: 'nowrap' }}
                        data-testid={`v2dev-revoke-${r.id}`}>Sign out</button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>No devices yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
